import { useRef } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, Upload } from 'lucide-react';
import { toast } from 'sonner';

export function WorkflowExportImport() {
  const { currentWorkflow, workflows, addWorkflow } = useWorkflowStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = () => {
    if (!currentWorkflow) {
      toast.error('No workflow selected');
      return;
    }

    const dataStr = JSON.stringify(currentWorkflow, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentWorkflow.name.replace(/\s+/g, '-')}-workflow.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Workflow exported successfully');
  };

  const handleExportAll = () => {
    if (workflows.length === 0) {
      toast.error('No workflows to export');
      return;
    }

    const dataStr = JSON.stringify(workflows, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflows-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('All workflows exported successfully');
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content);

        // Handle both single workflow and array of workflows
        if (Array.isArray(data)) {
          data.forEach((workflow) => {
            addWorkflow(workflow.name);
            useWorkflowStore.setState((state) => ({
              workflows: [...state.workflows, workflow],
            }));
          });
          toast.success(`Imported ${data.length} workflows`);
        } else {
          addWorkflow(data.name);
          useWorkflowStore.setState((state) => ({
            workflows: [...state.workflows, data],
          }));
          toast.success('Workflow imported successfully');
        }
      } catch (error) {
        toast.error('Failed to import workflow. Invalid file format.');
      }
    };
    reader.readAsText(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div>
        <h4 className="font-semibold text-foreground mb-2">Backup & Restore</h4>
        <p className="text-xs text-muted-foreground mb-4">
          Export your workflows to backup or share them. Import to restore from backup.
        </p>
      </div>

      <div className="space-y-2">
        <Button
          className="w-full"
          variant="outline"
          onClick={handleExport}
          disabled={!currentWorkflow}
        >
          <Download className="w-4 h-4 mr-2" />
          Export Current Workflow
        </Button>

        {workflows.length > 1 && (
          <Button className="w-full" variant="outline" onClick={handleExportAll}>
            <Download className="w-4 h-4 mr-2" />
            Export All Workflows
          </Button>
        )}

        <Button
          className="w-full"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-4 h-4 mr-2" />
          Import Workflow
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />
      </div>

      <div className="p-3 rounded bg-blue-500/5 border border-blue-500/20 text-xs text-blue-700">
        <p className="font-medium mb-1">File Format:</p>
        <p>Export files contain complete workflow configuration including all nodes, connections, and settings.</p>
      </div>
    </Card>
  );
}